import { cleanCorruptedAnnotation } from './ivone_utils.js';

/**
 * Numérotation d'inspection.
 *
 * 1. Filtrer les annotations selon les champs de la vue (annotation.data).
 * 2. Les trier sur la carte (haut/bas, gauche/droite).
 * 3. Produire la liste { id, number } à enregistrer.
 *
 * Le champ « Géométrie » de la vue n'utilise pas geometryName ni les coords directement.
 */

// —— 1. Lire la position sur la carte ——

function isPoint(pair) {
  return (
    Array.isArray(pair) &&
    pair.length >= 2 &&
    typeof pair[0] === 'number' &&
    typeof pair[1] === 'number' &&
    !Array.isArray(pair[0])
  );
}

/** Plusieurs formats possibles dans scantube : point, ligne, polygone. */
function readPointsFromGeometry(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return [];
  if (typeof coordinates[0] === 'number') return [[coordinates[0], coordinates[1]]];
  if (isPoint(coordinates[0])) return coordinates.map((c) => [c[0], c[1]]);
  const ring = coordinates[0];
  if (Array.isArray(ring) && isPoint(ring[0])) return ring.map((c) => [c[0], c[1]]);
  return [];
}

/** Points [x, y] de la forme dessinée (pour savoir si on peut numéroter / trier). */
export function getShapePoints(geometry) {
  return geometry?.coordinates ? readPointsFromGeometry(geometry.coordinates) : [];
}

function middleOfShape(geometry) {
  const points = getShapePoints(geometry);
  if (!points.length) return null;
  let x = 0;
  let y = 0;
  for (const [px, py] of points) {
    x += px;
    y += py;
  }
  return [x / points.length, y / points.length];
}

/** Comme Array.sort : négatif = a avant b, positif = a après b. */
function compareMapOrder(
  a,
  b,
  startFromTop,
  startFromLeft,
  numberRowsBeforeColumns
) {
  const posA = middleOfShape(a.geometry);
  const posB = middleOfShape(b.geometry);
  if (!posA && !posB) return 0;
  if (!posA) return 1;
  if (!posB) return -1;

  const compareVertical = () => {
    const diff = posA[1] - posB[1];
    return diff === 0 ? 0 : startFromTop ? -diff : diff;
  };
  const compareHorizontal = () => {
    const diff = posA[0] - posB[0];
    return diff === 0 ? 0 : startFromLeft ? diff : -diff;
  };

  if (numberRowsBeforeColumns) {
    const byRow = compareVertical();
    return byRow !== 0 ? byRow : compareHorizontal();
  }
  const byColumn = compareHorizontal();
  return byColumn !== 0 ? byColumn : compareVertical();
}

function sortAnnotationsOnMap(
  annotations,
  startFromTop,
  startFromLeft,
  sortPriority
) {
  const numberRowsBeforeColumns = sortPriority === 'vertical';
  return [...annotations].sort((a, b) =>
    compareMapOrder(a, b, startFromTop, startFromLeft, numberRowsBeforeColumns)
  );
}

// —— 2. Filtres (champs de la vue dans annotation.data) ——

function annotationMatchesFilter(annotation, viewFields, fieldName, chosenValue) {
  const field = viewFields.find((k) => k.name === fieldName);
  if (!field) return false;

  const chosenOption =
    field.type === 'select' && field.options
      ? field.options.find(
          (o) => o.id === chosenValue || String(o.text) === String(chosenValue)
        )
      : null;

  const valueInData = annotation.data?.[fieldName];
  if (chosenOption && valueInData != null && valueInData !== '') {
    if (
      String(valueInData) === String(chosenOption.id) ||
      String(valueInData) === String(chosenOption.text)
    ) {
      return true;
    }
  }

  const valueShown = cleanCorruptedAnnotation(viewFields, annotation, 'display').data[
    fieldName
  ];
  if (valueShown == null || valueShown === '' || valueShown === 'N/A') return false;

  const labelWeExpect = chosenOption?.text ?? chosenValue;
  return labelWeExpect != null && String(valueShown) === String(labelWeExpect);
}

/**
 * Ne garde que les annotations qui passent tous les filtres (ET).
 * mode « include » : doit correspondre ; « exclude » : ne doit pas correspondre.
 */
export function filterAnnotationsForNumbering(annotations, viewFields, filters) {
  const filtersToApply = (filters ?? []).filter(
    (f) => f.fieldKey && f.fieldValue != null && f.fieldValue !== ''
  );
  if (!filtersToApply.length) return annotations;

  const includeRules = filtersToApply.filter((f) => f.mode !== 'exclude');
  const excludeRules = filtersToApply.filter((f) => f.mode === 'exclude');

  // Includes are cumulative as "OR" (match at least one include rule),
  // Excludes are cumulative as "AND" (must match none of the exclude rules).
  return annotations.filter((annotation) => {
    const matchesRule = (rule) =>
      annotationMatchesFilter(annotation, viewFields, rule.fieldKey, rule.fieldValue);

    const passesExcludes = excludeRules.every((r) => !matchesRule(r));
    if (!passesExcludes) return false;

    const passesIncludes =
      includeRules.length === 0 ? true : includeRules.some((r) => matchesRule(r));
    return passesIncludes;
  });
}

// —— 3. Liste finale { id, number } ——

export function buildNumberingPlan(
  annotations,
  startFromTop,
  startFromLeft,
  firstNumber,
  sortPriority = 'vertical'
) {
  const withShape = annotations.filter((a) => getShapePoints(a.geometry).length > 0);
  const inOrder = sortAnnotationsOnMap(
    withShape,
    startFromTop,
    startFromLeft,
    sortPriority
  );
  return inOrder.map((a, index) => ({ id: a.id, number: firstNumber + index }));
}
